import { resourceActionTypes, userActionTypes } from '../actions/types';
import { resourceService, userService } from '../services';
import _ from 'lodash';
import { generateImageLink } from '../utils/design.utils';

const getGalleryDimensions = resource => {
  const width = Number(resource.width || _.get(resource, 'data.width'));
  const height = Number(resource.height || _.get(resource, 'data.height'));

  return {
    width: Number.isFinite(width) && width > 0 ? width : 1,
    height: Number.isFinite(height) && height > 0 ? height : 1,
  };
};

const resetState = () => {
  return dispatch => dispatch({ type: resourceActionTypes.RESET_STATE });
};

const upload = files => {
  return (dispatch, getState) => {
    dispatch(request(0));

    const user = getState().authentication.user;
    let userId = null;
    if (user.role === 'client') {
      userId = user.userId;
    }

    resourceService
      .upload(userId, files, progress => dispatch(request(progress)))
      .then(
        resource => {
          dispatch(success(resource));
        },
        error => {
          dispatch(
            _refreshTokenOnFailure(
              error,
              resourceActionTypes.UPLOAD_FAILURE,
              getState().authentication.pendingRefreshingToken,
            ),
          );
        },
      );
  };

  function request(progress) {
    return { type: resourceActionTypes.UPLOAD_REQUEST, progress };
  }
  function success(resource) {
    return { type: resourceActionTypes.UPLOAD_SUCCESS, resource };
  }
};

const search = (query, clear) => {
  return (dispatch, getState) => {
    dispatch(request(query.type, clear));

    const userId = getState().authentication.user.userId;
    let resourceType = query.type;
    let skip;
    let limit;
    const authToken = getState().authentication.user.authToken

    query = _.omitBy(query, value => _.isNil(value) || (_.isString(value) && _.isEmpty(value)));

    switch (resourceType) {
      case 'Text':
        skip = getState().resource.textSkip;
        limit = getState().resource.textLimit;
        break;
      case 'Image':
        skip = getState().resource.imageSkip;
        limit = getState().resource.imageLimit;
        query = { ...query, educationOnly: true };
        break;
      case 'Video':
        skip = getState().resource.videoSkip;
        limit = getState().resource.videoLimit;
        break;
      case 'Element':
        skip = getState().resource.elementSkip;
        limit = getState().resource.elementLimit;
        delete query.type;
        query = { ...query, type: 'Shape,Vector,Frame,Grid,Line', educationOnly: true };
        break;
      case 'Background':
        skip = getState().resource.backgroundSkip;
        limit = getState().resource.backgroundLimit;
        break;
      case 'Font':
        skip = getState().resource.fontSkip;
        limit = getState().resource.fontLimit;
        break;
    }

    if (clear || skip === null) {
      skip = 0;
    } else {
      skip = skip + limit;
    }

    resourceService
      .findAll(null, {
        ...query,
        skip,
        limit,
        perGroupLimit: 5,
      })
      .then(
        resources => {
          if (_.has(resources, '[0].resources')) {
            resources = resources.map(resourceGroup => {
              resourceGroup.resources = resourceGroup.resources.map((resource, index) => {
                const thumbnailText = resourceType === 'Video' ? 'frame' : 'thumbnail';
                const resourceName = resourceType === 'Text' ? resource._id : resource.name;
                const { width, height } = getGalleryDimensions(resource);
                const srcThumbnail = generateImageLink(`${process.env.REACT_APP_FILE_SERVER
                  }/resources/${resource.type.toLowerCase()}s/${resourceName}/${thumbnailText}.${resource.extension}`, authToken);
                const src = srcThumbnail.replace(thumbnailText, 'screen');
                return {
                  src: srcThumbnail,
                  width,
                  height,
                  key: index.toString(),
                  type: resource.type,
                  data: {
                    id: resource._id,
                    src,
                    srcThumbnail,
                    width,
                    height,
                    tags: resource.tags,
                    liked: _.get(resource, 'liked', []).indexOf(userId) !== -1,
                    price: resource.price,
                    ...resource.data,
                  },
                };
              });
              return resourceGroup;
            });
          } else {
            const offset = clear ? 0 : _.get(getState().resource.resources, 'length', 0);
            resources = resources.map((resource, index) => {
              const thumbnailText = resourceType === 'Video' ? 'frame' : 'thumbnail';
              const resourceName = resourceType === 'Text' ? resource._id : resource.name;
              const authToken = getState().authentication.user.authToken
              const { width, height } = getGalleryDimensions(resource);

              const srcThumbnail = generateImageLink(`${process.env.REACT_APP_FILE_SERVER
                }/resources/${resource.type.toLowerCase()}s/${resourceName}/${thumbnailText}.${resource.extension}`, authToken);
              const src = srcThumbnail.replace(thumbnailText, 'screen');
              return {
                src: srcThumbnail,
                width,
                height,
                key: (offset + index).toString(),
                type: resource.type,
                data: {
                  id: resource._id,
                  src,
                  srcThumbnail,
                  name: resource.name,
                  extension: resource.extension,
                  width,
                  height,
                  tags: resource.tags,
                  liked: _.get(resource, 'liked', []).indexOf(userId) !== -1,
                  price: resource.price,
                  ...resource.data,
                },
              };
            });
          }
          dispatch(success(resourceType, resources, skip, limit, clear));
        },
        error => {
          dispatch(
            _refreshTokenOnFailure(
              error,
              resourceActionTypes.SEARCH_FAILURE,
              getState().authentication.pendingRefreshingToken,
              { resourceType },
            ),
          );
        },
      );
  };

  function request(resourceType, clear) {
    return { type: resourceActionTypes.SEARCH_REQUEST, resourceType, clear };
  }
  function success(resourceType, resources, skip, limit, clear) {
    return {
      type: resourceActionTypes.SEARCH_SUCCESS,
      resourceType,
      resources,
      skip,
      limit,
      clear,
    };
  }
};

const getImageTags = () => {
  return (dispatch, getState) => {
    const authToken = getState().authentication.user.authToken
    const query = {
      type: 'Image',
      grouped: true,
      perGroupLimit: 1,
      educationOnly: true,
    };

    resourceService.findAll(null, { ...query }).then(
      resources => {
        const imageTags = [];
        resources.forEach((resourceGroup, index) => {
          resourceGroup.resources.forEach(resource => {
            const { width, height } = getGalleryDimensions(resource);
            const srcThumbnail = generateImageLink(`${process.env.REACT_APP_FILE_SERVER}/resources/${resource.type.toLowerCase()}s/${resource.name
              }/thumbnail.${resource.extension}`, authToken);
            const src = srcThumbnail.replace('thumbnail', 'screen');
            imageTags.push({
              src: srcThumbnail,
              width,
              height,
              key: index.toString(),
              type: resource.type,
              data: {
                id: resource._id,
                src,
                srcThumbnail,
                width,
                height,
                tags: resource.tags,
              },
            });
          });
        });
        dispatch(success(imageTags));
      },
      error => {
        dispatch(
          _refreshTokenOnFailure(
            error,
            resourceActionTypes.IMAGE_TAGS_FAILURE,
            getState().authentication.pendingRefreshingToken,
          ),
        );
      },
    );
  };

  function request() {
    return { type: resourceActionTypes.IMAGE_TAGS_REQUEST };
  }
  function success(imageTags) {
    return {
      type: resourceActionTypes.IMAGE_TAGS_SUCCESS,
      imageTags,
    };
  }
};

const getUserResource = type => {
  return (dispatch, getState) => {
    dispatch(request());

    const user = getState().authentication.user;
    let userId = null;
    if (user.role === 'client') {
      userId = user.userId;
    }

    let resourceType = type;
    let skip;
    let limit;

    switch (resourceType) {
      case 'Image':
        skip = getState().resource.userImageSkip;
        limit = getState().resource.userImageLimit;
        break;
      case 'Video':
        skip = getState().resource.userVideoSkip;
        limit = getState().resource.userVideoLimit;
        break;
    }

    if (skip === null) {
      skip = 0;
    } else {
      skip = skip + limit;
    }

    resourceService
      .findAll(userId, {
        type,
        skip,
        limit,
      })
      .then(
        resources => {
          dispatch(success(resourceType, resources, skip, limit));
        },
        error => {
          dispatch(
            _refreshTokenOnFailure(
              error,
              resourceActionTypes.USER_RESOURCE_FAILURE,
              getState().authentication.pendingRefreshingToken,
            ),
          );
        },
      );
  };

  function request() {
    return { type: resourceActionTypes.USER_RESOURCE_REQUEST };
  }
  function success(resourceType, resources, skip, limit) {
    return {
      type: resourceActionTypes.USER_RESOURCE_SUCCESS,
      resourceType,
      resources,
      skip,
      limit,
    };
  }
};

const deleteResource = (type, resourceId) => {
  return (dispatch, getState) => {
    dispatch(request());

    const user = getState().authentication.user;
    let userId = null;
    if (user.role === 'client') {
      userId = user.userId;
    }

    resourceService._delete(userId, resourceId).then(
      () => {
        let resources;
        switch (type) {
          case 'UserImage':
            resources = [...getState().resource.userImages];
            _.remove(resources, resource => {
              return resource._id === resourceId;
            });
        }
        dispatch(success(type, resources));
      },
      error => {
        dispatch(
          _refreshTokenOnFailure(
            error,
            resourceActionTypes.DELETE_FAILURE,
            getState().authentication.pendingRefreshingToken,
          ),
        );
      },
    );
  };

  function request() {
    return { type: resourceActionTypes.DELETE_REQUEST };
  }
  function success(resourceType, resources) {
    return {
      type: resourceActionTypes.DELETE_SUCCESS,
      resourceType,
      resources,
    };
  }
};

const getUserFolders = () => {
  return (dispatch, getState) => {
    dispatch(request());

    const userId = getState().authentication.user.userId;

    userService.findAllFolders(userId).then(
      folders => {
        dispatch(success(folders));
      },
      error => {
        dispatch(
          _refreshTokenOnFailure(
            error,
            resourceActionTypes.USER_FOLDERS_FAILURE,
            getState().authentication.pendingRefreshingToken,
          ),
        );
      },
    );
  };

  function request() {
    return { type: resourceActionTypes.USER_FOLDERS_REQUEST };
  }
  function success(folders) {
    return {
      type: resourceActionTypes.USER_FOLDERS_SUCCESS,
      folders,
    };
  }
};

const getFolderResources = (folderId, clear) => {
  return (dispatch, getState) => {
    dispatch(request());

    const userId = getState().authentication.user.userId;

    let skip = getState().resource[`${folderId}Skip`];
    let limit = getState().resource[`${folderId}Limit`];
    if (!limit) {
      limit = 16;
    }

    if (clear || skip === null) {
      skip = 0;
    } else {
      skip = skip + limit;
    }

    resourceService.findAllInFolder(userId, folderId, { skip, limit }).then(
      result => {
        const offset = clear ? 0 : _.get(getState().resource, `${folderId}s`, []).length;
        let resources = _.get(result, '[0].folders.[0].resources', []);
        resources = resources.map((resource, index) => {
          const thumbnailText = resource.type === 'Video' ? 'frame' : 'thumbnail';
          const { width, height } = getGalleryDimensions(resource);
          const srcThumbnail = resource.private
            ? `${process.env.REACT_APP_FILE_SERVER}/users/${userId}/resources/${resource.name}/${thumbnailText}.${resource.extension}`
            : `${process.env.REACT_APP_FILE_SERVER}/resources/${resource.type.toLowerCase()}s/${resource.name
            }/${thumbnailText}.${resource.extension}`;
          const src = srcThumbnail.replace(thumbnailText, 'screen');
          return {
            src: srcThumbnail,
            width,
            height,
            key: (offset + index).toString(),
            type: resource.type,
            data: {
              id: resource._id,
              src,
              srcThumbnail,
              width,
              height,
              tags: resource.tags,
              liked: _.get(resource, 'liked', []).indexOf(userId) !== -1,
            },
          };
        });
        dispatch(success(folderId, resources, skip, limit, clear));
      },
      error => {
        dispatch(
          _refreshTokenOnFailure(
            error,
            resourceActionTypes.USER_FOLDER_RESOURCES_FAILURE,
            getState().authentication.pendingRefreshingToken,
          ),
        );
      },
    );
  };

  function request() {
    return { type: resourceActionTypes.USER_FOLDER_RESOURCES_REQUEST };
  }
  function success(folderId, resources, skip, limit, clear) {
    return {
      type: resourceActionTypes.USER_FOLDER_RESOURCES_SUCCESS,
      folderName: folderId,
      resources,
      skip,
      limit,
      clear,
    };
  }
};

const createFolder = folderName => {
  return (dispatch, getState) => {
    dispatch(request());

    const user = getState().authentication.user;
    let userId = null;
    if (user.role === 'client') {
      userId = user.userId;
    }

    userService.createFolder(userId, folderName).then(
      folders => {
        dispatch(success(folders));
      },
      error => {
        dispatch(
          _refreshTokenOnFailure(
            error,
            resourceActionTypes.CREATE_FOLDER_FAILURE,
            getState().authentication.pendingRefreshingToken,
          ),
        );
      },
    );
  };

  function request() {
    return { type: resourceActionTypes.CREATE_FOLDER_REQUEST };
  }
  function success(folders) {
    return {
      type: resourceActionTypes.CREATE_FOLDER_SUCCESS,
      folders,
    };
  }
};

const deleteFolder = folderId => {
  return (dispatch, getState) => {
    dispatch(request());

    const user = getState().authentication.user;
    let userId = null;
    if (user.role === 'client') {
      userId = user.userId;
    }

    userService.deleteFolder(userId, folderId).then(
      folders => {
        dispatch(success(folders));
      },
      error => {
        dispatch(
          _refreshTokenOnFailure(
            error,
            resourceActionTypes.DELETE_FOLDER_FAILURE,
            getState().authentication.pendingRefreshingToken,
          ),
        );
      },
    );
  };

  function request() {
    return { type: resourceActionTypes.DELETE_FOLDER_REQUEST };
  }
  function success(folders) {
    return {
      type: resourceActionTypes.DELETE_FOLDER_SUCCESS,
      folders,
    };
  }
};

const addToFolder = (folderId, resource) => {
  return (dispatch, getState) => {
    dispatch(request());

    const user = getState().authentication.user;
    let userId = null;
    if (user.role === 'client') {
      userId = user.userId;
    }

    resourceService.userFolderAction(userId, resource.data.id, folderId, 'add').then(
      () => {
        dispatch(success(folderId, resource));
      },
      error => {
        dispatch(
          _refreshTokenOnFailure(
            error,
            resourceActionTypes.ADD_TO_FOLDER_FAILURE,
            getState().authentication.pendingRefreshingToken,
          ),
        );
      },
    );
  };

  function request() {
    return { type: resourceActionTypes.ADD_TO_FOLDER_REQUEST };
  }
  function success(folderId, resource) {
    return { type: resourceActionTypes.ADD_TO_FOLDER_SUCCESS, folderId, resource };
  }
};

const removeFromFolder = (folderId, resourceId, index) => {
  return (dispatch, getState) => {
    dispatch(request());

    const user = getState().authentication.user;
    let userId = null;
    if (user.role === 'client') {
      userId = user.userId;
    }

    resourceService.userFolderAction(userId, resourceId, folderId, 'remove').then(
      () => {
        const folderResources = [...getState().resource[`${folderId.toLowerCase()}s`]];
        folderResources.splice(index, 1);
        dispatch(success(folderId, folderResources));
      },
      error => {
        dispatch(
          _refreshTokenOnFailure(
            error,
            resourceActionTypes.REMOVE_FROM_FOLDER_FAILURE,
            getState().authentication.pendingRefreshingToken,
          ),
        );
      },
    );
  };

  function request() {
    return { type: resourceActionTypes.REMOVE_FROM_FOLDER_REQUEST };
  }
  function success(folderId, resources) {
    return { type: resourceActionTypes.REMOVE_FROM_FOLDER_SUCCESS, folderId, resources };
  }
};

const userAction = (type, resourceId, action) => {
  return (dispatch, getState) => {
    dispatch(request());

    const userId = getState().authentication.user.userId;

    resourceService.userAction(userId, resourceId, action).then(
      () => {
        const resources = [...getState().resource[`${type.toLowerCase()}s`]];
        if (_.has(resources, '[0].resources')) {
          resources.map(resourceGroup => {
            resourceGroup.resources.map(resource => {
              if (resource.data.id === resourceId) {
                switch (action) {
                  case 'like':
                    resource.data.liked = true;
                    break;
                  case 'unlike':
                    resource.data.liked = false;
                    break;
                }
              }
            });
          });
        } else {
          resources.map(resource => {
            if (resource.data.id === resourceId) {
              switch (action) {
                case 'like':
                  resource.data.liked = true;
                  break;
                case 'unlike':
                  resource.data.liked = false;
                  break;
              }
            }
          });
        }
        dispatch(success(type, resources));
      },
      error => {
        dispatch(
          _refreshTokenOnFailure(
            error,
            resourceActionTypes.USER_ACTION_FAILURE,
            getState().authentication.pendingRefreshingToken,
          ),
        );
      },
    );
  };

  function request() {
    return { type: resourceActionTypes.USER_ACTION_REQUEST };
  }
  function success(resourceType, resources) {
    return {
      type: resourceActionTypes.USER_ACTION_SUCCESS,
      resourceType,
      resources,
    };
  }
};

const getStatistics = latest => {
  return (dispatch, getState) => {
    dispatch(request());

    resourceService.getStatistics(latest).then(
      statistics => {
        let resources = {};
        statistics.map(resource => {
          resources[resource._id] = resource.count;
        });
        dispatch(success(resources));
      },
      error => {
        // dispatch(error);
      },
    );
  };

  function request() {
    if (latest) {
      return { type: resourceActionTypes.STATISTIC_LATEST_REQUEST };
    } else {
      return { type: resourceActionTypes.STATISTIC_REQUEST };
    }
  }
  function success(resources) {
    if (latest) {
      return {
        type: resourceActionTypes.STATISTIC_LATEST_SUCCESS,
        resources,
      };
    } else {
      return {
        type: resourceActionTypes.STATISTIC_SUCCESS,
        resources,
      };
    }
  }
};

const _refreshTokenOnFailure = (error, failureAction, pendingRefreshToken, restParams = {}) => {
  if (error.error === 'jwt expired') {
    if (pendingRefreshToken) {
      return {
        type: userActionTypes.PENDING_REFRESH_TOKEN,
        error,
      };
    } else {
      return { type: userActionTypes.INVALID_TOKEN, error };
    }
  } else {
    return { type: failureAction, error, ...restParams };
  }
};

export const resourceActions = {
  resetState,
  upload,
  search,
  getUserResource,
  deleteResource,
  userAction,
  getUserFolders,
  getFolderResources,
  createFolder,
  deleteFolder,
  addToFolder,
  removeFromFolder,
  getImageTags,
  getStatistics,
};
